/**
 * MacroGeneratorPage
 * Interactive tool for building and previewing MomentScience macros
 */

import React, { useState, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Home, Wand2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Macro, MacroTestData } from '../types/macros'
import { clearMacros } from '../utils/macroHelpers'
import MacroWizard from '../components/macro-generator/MacroWizard'
import MacroList from '../components/macro-generator/MacroList'
import MacroPreview from '../components/macro-generator/MacroPreview'
import CodeExport from '../components/macro-generator/CodeExport'
import ResizeHandle from '../components/ResizeHandle'
import { useToast } from '../components/ui/ToastProvider'
import logoIcon from '../assets/images/logo-icon.png'

const MacroGeneratorPage: React.FC = () => {
  const [macros, setMacros] = useState<Macro[]>([])
  const [testData, setTestData] = useState<MacroTestData>({})
  const [leftPanelWidth, setLeftPanelWidth] = useState(40)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { showSuccess } = useToast()

  const handleAddMacro = (macro: Macro) => {
    setMacros(prev => [...prev, macro])
    showSuccess('Macro added successfully')
  }

  const handleDeleteMacro = (id: string) => {
    setMacros(prev => prev.filter(m => m.id !== id))
    showSuccess('Macro removed')
  }

  const handleClearAll = () => {
    setMacros([])
    setTestData({})
    clearMacros()
    showSuccess('All macros cleared')
  }

  const handleUpdateTestData = (key: string, value: string) => {
    setTestData(prev => ({ ...prev, [key]: value }))
  }

  const handleRemoveTestDataKey = (key: string) => {
    setTestData(prev => {
      const newData = { ...prev }
      delete newData[key]
      return newData
    })
  }

  const onResizeLeft = useCallback((delta: number) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      const deltaPercent = (delta / containerWidth) * 100
      setLeftPanelWidth(prev => {
        const newWidth = prev + deltaPercent
        return Math.max(30, Math.min(70, newWidth))
      })
    }
  }, [])

  const rightPanelWidth = 100 - leftPanelWidth

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <motion.div
        className="sticky top-0 z-40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50 shadow-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            {/* Left Section - Logo and Title */}
            <div className="flex items-center space-x-3">
              <div className="relative flex-shrink-0">
                <img
                  src={logoIcon}
                  alt="MomentScience"
                  className="w-8 h-8 md:w-9 md:h-9 object-contain drop-shadow-sm"
                />
              </div>
              <div className="flex flex-col -space-y-0.5">
                <h1 className="text-base md:text-lg font-bold text-[#FE4215] dark:text-[#FF6B35] tracking-tight">
                  MomentScience Labs
                </h1>
                <h2 className="text-sm font-medium text-gray-600 dark:text-gray-400 tracking-wide flex items-center space-x-2">
                  <Wand2 className="w-4 h-4" />
                  <span>Macro Generator</span>
                </h2>
              </div>
            </div>

            {/* Right Section - Controls */}
            <div className="flex items-center space-x-2">
              {/* Home Button */}
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-1.5 px-3 md:px-4 py-2 bg-gradient-to-r from-[#FE4215] to-[#FF6B35] hover:from-[#E5350A] hover:to-[#FF6B35] text-white rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-[#FE4215]/25 group"
                title="Go to home"
              >
                <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium hidden sm:inline">Home</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content - Split View */}
      <div ref={containerRef} className="flex h-[calc(100vh-80px)] overflow-hidden">
        {/* Left Panel - Builder & List */}
        <div
          style={{ width: macros.length === 0 ? '100%' : `${leftPanelWidth}%` }}
          className={`flex flex-col overflow-hidden ${macros.length > 0 ? 'border-r border-gray-200 dark:border-gray-700' : ''}`}
        >
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <MacroWizard onAddMacro={handleAddMacro} />
            </motion.div>

            {/* Show MacroList only when there are macros */}
            {macros.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <MacroList
                  macros={macros}
                  onDeleteMacro={handleDeleteMacro}
                  onClearAll={handleClearAll}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* Resize Handle - Only show when macros exist */}
        {macros.length > 0 && <ResizeHandle direction="horizontal" onResize={onResizeLeft} />}

        {/* Right Panel - Preview & Export - Only show when macros exist */}
        {macros.length > 0 && (
          <div
            style={{ width: `${rightPanelWidth}%` }}
            className="flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <MacroPreview
                  macros={macros}
                  testData={testData}
                  onUpdateTestData={handleUpdateTestData}
                  onRemoveTestDataKey={handleRemoveTestDataKey}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CodeExport macros={macros} testData={testData} />
              </motion.div>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default MacroGeneratorPage
